import { Modal, Select, Tag } from 'antd';
import React from 'react';
import { PeopleInterface } from 'type/peopleType';

// Enhanced Cast Editor Component (editable)
interface EnhancedCastEditorProps {
  value?: { cast: number; type: string }[];
  onChange?: (value: { cast: number; type: string }[]) => void;
  peopleList?: PeopleInterface[];
  onSearchPeople?: (searchTerm: string) => void;
}

const EnhancedCastEditor: React.FC<EnhancedCastEditorProps> = ({
  value = [],
  onChange,
  peopleList = [],
  onSearchPeople
}) => {
  // Define cast types to use (only 3 from existing typeLabels)
  const castTypes = [
    { value: 'director', label: 'Director' },
    { value: 'writer', label: 'Writer' },
    { value: 'actor', label: 'Actor' }
  ];

  // Cast data grouped by type
  const groupedCast = castTypes.reduce((acc, typeOption) => {
    acc[typeOption.value] = value.filter(item => item.type === typeOption.value);
    return acc;
  }, {} as Record<string, { cast: number; type: string }[]>);

  // Convert cast ID to name
  const getCastName = (castId: number): string => {
    const person = peopleList.find(p => p.id === castId);
    return person ? person.name : `Cast ID: ${castId}`;
  };

  // 동일 인물 판별용 정규화 이름 키 (name_en > i18n_name.en > name). 못 찾으면 빈 문자열.
  const nameKeyOf = (castId: number): string => {
    const person = peopleList.find(p => p.id === castId);
    if (!person) return '';
    return (person.name_en || person.i18n_name?.en || person.name || '').trim().toLowerCase();
  };

  // 사람에게 보여줄 이름
  const displayNameOf = (castId: number): string => {
    const person = peopleList.find(p => p.id === castId);
    return person?.i18n_name?.en || person?.name || `Cast ID: ${castId}`;
  };

  // Handler for cast changes of a specific type
  const handleCastChange = (type: string, selectedCastIds: number[]) => {
    // Keep casts of other types
    const otherTypeCasts = value.filter(item => item.type !== type);

    // Add newly selected casts as the specified type
    const newTypeCasts = selectedCastIds.map(castId => ({
      cast: castId,
      type: type
    }));

    // Update the entire enhanced_cast array
    const updatedValue = [...otherTypeCasts, ...newTypeCasts];

    // 이번에 새로 추가된 cast (기존 같은 type 선택에 없던 것)
    const prevIds = value.filter(item => item.type === type).map(item => item.cast);
    const addedIds = selectedCastIds.filter(id => !prevIds.includes(id));

    // 새로 추가된 것 중, 서로 다른 cast_id 인데 같은 인물(name_en)이 이미 리스트에 있는 경우
    const duplicateAddedIds = addedIds.filter(addedId => {
      const key = nameKeyOf(addedId);
      if (!key) return false;
      return updatedValue.some(
        item => item.cast !== addedId && nameKeyOf(item.cast) === key
      );
    });

    if (duplicateAddedIds.length > 0) {
      const names = Array.from(new Set(duplicateAddedIds.map(displayNameOf))).join(', ');
      Modal.confirm({
        title: '동일 인물 중복 추가',
        content: `이미 같은 인물(${names})이(가) 추가되어 있습니다. 그래도 추가하시겠습니까?`,
        okText: '추가',
        cancelText: '취소',
        onOk: () => onChange?.(updatedValue),
        onCancel: () => {
          // 중복으로 새로 추가된 것만 제외하고 반영
          const filtered = updatedValue.filter(
            item => !(item.type === type && duplicateAddedIds.includes(item.cast))
          );
          onChange?.(filtered);
        },
      });
      return;
    }

    onChange?.(updatedValue);
  };

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px' }}>
      {castTypes.map(typeOption => {
        const currentCasts = groupedCast[typeOption.value] || [];
        const currentCastIds = currentCasts.map(item => item.cast);

        return (
          <div key={typeOption.value} style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Tag color="blue" style={{ fontSize: '12px', padding: '2px 8px' }}>
                {typeOption.label}
              </Tag>
            </div>
            <Select
              mode="multiple"
              showSearch
              style={{ width: '100%' }}
              placeholder={`Search and select ${typeOption.label}`}
              value={currentCastIds}
              onChange={(selectedIds: number[]) => handleCastChange(typeOption.value, selectedIds)}
              onSearch={onSearchPeople}
              filterOption={false}
              options={peopleList.map(person => ({
                label: person.i18n_name?.en || person.name,
                value: person.id,
              }))}
              tagRender={({ label, value, closable, onClose }) => {
                const person = peopleList.find(p => p.id === value);
                const displayName = person?.i18n_name?.en || person?.name || label;

                return (
                  <span
                    onMouseDown={e => {
                      // Stop event propagation only if not clicking the close button
                      if ((e.target as HTMLElement).closest('.ant-tag-close-icon')) {
                        return;
                      }
                      // Stop event propagation to prevent Select from opening on click for text selection
                      e.stopPropagation();
                    }}
                    onMouseUp={e => {
                      // Stop event propagation only if not clicking the close button
                      if ((e.target as HTMLElement).closest('.ant-tag-close-icon')) {
                        return;
                      }
                      // Prevent Select from grabbing focus after text selection completes
                      e.stopPropagation();
                      // Add a slight delay to allow text selection to complete
                      setTimeout(() => {
                        // Prevent Select from grabbing focus even after text selection is complete
                        const selection = window.getSelection();
                        if (selection && selection.toString().length > 0) {
                          // Prevent Select focus if text is selected
                          e.stopPropagation();
                        }
                      }, 0);
                    }}
                    onClick={e => {
                      // Stop event propagation only if not clicking the close button
                      if ((e.target as HTMLElement).closest('.ant-tag-close-icon')) {
                        return;
                      }
                      // Prevent Select from opening on click after text selection
                      e.stopPropagation();
                    }}
                    style={{ display: 'inline-block' }}
                  >
                    <Tag
                      closable={closable}
                      onClose={onClose}
                      style={{
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        MozUserSelect: 'text',
                        msUserSelect: 'text',
                        cursor: 'text',
                      }}
                    >
                      {displayName}
                    </Tag>
                  </span>
                );
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default EnhancedCastEditor;

